/**
 * Small read-only map that draws one neighborhood's boundary polygon. Used in
 * the admin dashboard so staff can see the imported border at a glance.
 */

import { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps } from '../../../utils/googleMapsLoader';
import { geojsonToPaths, type GeoJSONGeometry } from '../../../services/neighborhoodMapService';

interface Props {
  boundary: GeoJSONGeometry | null;
  center?: { lat: number; lng: number } | null;
  height?: number;
}

export default function AreaBoundaryMap({ boundary, center, height = 240 }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then(() => {
        if (cancelled || !ref.current) return;
        const map = new google.maps.Map(ref.current, {
          center: center || { lat: 24.7136, lng: 46.6753 },
          zoom: 12,
          disableDefaultUI: true,
          gestureHandling: 'cooperative',
        });
        if (boundary) {
          const paths = geojsonToPaths(boundary);
          if (paths.length) {
            const polygon = new google.maps.Polygon({
              paths,
              strokeColor: '#003930', strokeOpacity: 0.9, strokeWeight: 2,
              fillColor: '#004D40', fillOpacity: 0.12, map,
            });
            const bounds = new google.maps.LatLngBounds();
            paths.forEach((ring) => ring.forEach((pt) => bounds.extend(pt)));
            map.fitBounds(bounds);
            polygon.setMap(map);
          }
        }
      })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [boundary, center]);

  if (error) {
    return <div className="rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-400 text-sm" style={{ height }}>Map unavailable</div>;
  }
  return <div ref={ref} className="rounded-xl overflow-hidden border border-gray-200" style={{ height }} />;
}
